import { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { maskDateBR, brToIso, isoToBr, isValidBRDate } from "@/lib/format";

/**
 * DateInputBR
 * - Exibe e recebe input do usuário no formato DD/MM/AAAA com máscara automática (barras).
 * - Aceita `value` no formato ISO (YYYY-MM-DD) OU DD/MM/AAAA.
 * - Emite `onChange(isoOrEmpty)` — sempre em YYYY-MM-DD para manter compatibilidade com o backend.
 *   Se o usuário limpar o campo, emite "".
 *   Enquanto a data ainda não estiver completa/válida, mantém o último ISO válido (não emite lixo).
 * - Mostra borda vermelha e mensagem discreta quando a data estiver inválida.
 *
 * Props:
 *   value, onChange, placeholder, disabled, className, testId,
 *   min (ISO YYYY-MM-DD), max (ISO YYYY-MM-DD), required
 */
export default function DateInputBR({
  value,
  onChange,
  placeholder = "DD/MM/AAAA",
  disabled = false,
  className = "",
  testId,
  min,
  max,
  required = false,
  onBlur,
  ...rest
}) {
  const initialBR = useMemo(() => isoToBr(value), [value]);
  const [text, setText] = useState(initialBR);

  // Sincroniza quando o value externo muda (ex.: edição carregada)
  useEffect(() => {
    const next = isoToBr(value);
    if (next !== text) setText(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const invalid =
    !!text && (text.length !== 10 || !isValidBRDate(text)) && text.length >= 10;

  // Bloqueio simples por min/max
  const outOfRange = useMemo(() => {
    if (!text || text.length !== 10 || !isValidBRDate(text)) return false;
    const iso = brToIso(text);
    if (min && iso < min) return true;
    if (max && iso > max) return true;
    return false;
  }, [text, min, max]);

  const showError = invalid || outOfRange;

  const handleChange = (e) => {
    const raw = e.target.value;
    const masked = maskDateBR(raw);
    setText(masked);
    if (masked === "") {
      onChange && onChange("");
      return;
    }
    if (masked.length === 10 && isValidBRDate(masked)) {
      const iso = brToIso(masked);
      if (min && iso < min) return;
      if (max && iso > max) return;
      onChange && onChange(iso);
    }
    // Se ainda está digitando (parcial), não emitimos — o pai mantém o último valor
  };

  const handleKeyDown = (e) => {
    // Permitir apagar rapidamente as barras
    if (e.key === "Backspace" && text.endsWith("/")) {
      e.preventDefault();
      setText(text.slice(0, -1));
    }
  };

  const handleBlur = (e) => {
    // No blur, se digitou parcial inválido, mantém como está para o usuário corrigir
    onBlur && onBlur(e);
  };

  return (
    <div className="w-full">
      <Input
        type="text"
        inputMode="numeric"
        autoComplete="off"
        value={text}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder}
        disabled={disabled}
        maxLength={10}
        aria-invalid={showError || undefined}
        data-testid={testId}
        className={`${className} ${showError ? "border-red-400 focus-visible:ring-red-300" : ""}`}
        required={required}
        {...rest}
      />
      {showError && (
        <div className="mt-1 text-[11px] text-red-600" data-testid={testId ? `${testId}-error` : undefined}>
          {invalid ? "Data inválida (use DD/MM/AAAA)" : "Data fora do intervalo permitido"}
        </div>
      )}
    </div>
  );
}
